import { assert } from 'assert-ts';
import { LocalAllowListForm } from '#components/patterns/config-overrides/forms/LocalAllowForm.tsx';
import { RemoteAllowListForm } from '#components/patterns/config-overrides/forms/RemoteAllowForm.tsx';
import type { ConfigKey } from '#components/patterns/config-overrides/labels/ConfigOverrideLabels.ts';
import type { ConfigOverride } from '#dn-types/host.ts';
import { mapOverridesToForm } from '#screens/hosts/form/advanced/mappers/mapOverridesToForm.ts';
import { ConfigOverrideForm } from '#screens/tags/form/config-overrides/override-form/ConfigOverrideForm.tsx';
import { GraphiteForm } from '#screens/tags/form/config-overrides/stats-forms/GraphiteForm.tsx';
import { PrometheusForm } from '#screens/tags/form/config-overrides/stats-forms/PrometheusForm.tsx';
import { makeUniqueCIDRRequiredValidator } from '#util/validation/validators/uniqueCIDRRequired.ts';
import { makeUniqueNameRequiredValidator } from '#util/validation/validators/uniqueNameRequired.ts';

type Props = {
  onSave: (values: ConfigOverride[]) => void;
  configType: ConfigKey;
  value: ConfigOverride;
  stats: ReturnType<typeof mapOverridesToForm>['stats'];
  remoteCIDRs: string[];
  localAllowNames: string[];
};

export function EditConfigOverrideFormSwitch({
  onSave,
  configType,
  value,
  stats,
  remoteCIDRs,
  localAllowNames,
}: Props) {
  if (configType === 'lighthouse.remote_allow_list' && value.key === 'lighthouse.remote_allow_list') {
    return (
      <RemoteAllowListForm
        // We can take the first item because we edit a single rule from the tag form
        defaultValues={mapOverridesToForm([value]).lighthouse.remote_allow_list[0]}
        onSave={(data) => {
          onSave([{ key: configType, value: { [data.cidr]: data.allow } }]);
        }}
        cidrValidation={makeUniqueCIDRRequiredValidator(
          remoteCIDRs,
          // It's fine to grab the first one, because we only save one cidr per row
          remoteCIDRs.indexOf(assert(Object.keys(value.value)[0]))
        )}
      />
    );
  }

  if (configType === 'lighthouse.local_allow_list' && value.key === 'lighthouse.local_allow_list') {
    return (
      <LocalAllowListForm
        // We can take the first item because we edit a single rule from the tag form
        defaultValues={mapOverridesToForm([value]).lighthouse.local_allow_list[0]}
        onSave={(data) => {
          if ('cidr' in data) {
            onSave([{ key: configType, value: { interfaces: {}, [data.cidr]: data.allow } }]);
          } else {
            onSave([{ key: configType, value: { interfaces: { [data.interface]: data.allow } } }]);
          }
        }}
        cidrValidation={makeUniqueNameRequiredValidator(
          localAllowNames,
          localAllowNames.findIndex((l) => {
            const { interfaces, ...cidrs } = value.value;
            if (Object.keys(interfaces).length) {
              return Object.keys(interfaces)[0] === l;
            }
            return Object.keys(cidrs)[0] === l;
          })
        )}
      />
    );
  }

  if (configType === 'prometheus') {
    assert(stats.type === 'prometheus', 'Expected configType and stats.type to match');
    return <PrometheusForm defaultValues={{ stats }} onSave={onSave} />;
  }

  if (configType === 'graphite') {
    assert(stats.type === 'graphite', 'Expected configType and stats.type to match');
    return <GraphiteForm defaultValues={{ stats }} onSave={onSave} />;
  }

  return <ConfigOverrideForm overrideKey={value.key} defaultValues={[value]} onSave={onSave} />;
}
